import { useCallback, useEffect, useRef, useState } from "react";
import { isApiError } from "./api.ts";

export type LoadState<T> =
  | { status: "loading" }
  | { status: "loaded"; data: T }
  | { status: "error"; error: unknown };

// テストで差し替えられるよう、遷移は関数を経由する
export const redirect = { to: (url: string) => window.location.assign(url) };

/**
 * 画面を開いたときの読み込み。セッションが切れていたら（401 と loginUrl）ログインへ遷移する。
 * load は毎回同じ関数を渡す（変わると読み込み直す）
 */
export function useLoader<T>(load: () => Promise<T>) {
  const [state, setState] = useState<LoadState<T>>({ status: "loading" });
  const [version, setVersion] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  // biome-ignore lint/correctness/useExhaustiveDependencies: version を変えて読み込み直す
  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    loadRef.current().then(
      (data) => {
        if (active) setState({ status: "loaded", data });
      },
      (error: unknown) => {
        if (!active) return;
        if (isApiError(error, "UNAUTHENTICATED") && error.loginUrl !== null) {
          redirect.to(error.loginUrl);
        }
        setState({ status: "error", error });
      }
    );
    return () => {
      active = false;
    };
  }, [version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { state, setState, reload };
}
